import { SectionHeader } from '@/components/layout/SectionHeader';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, ArrowUpRight, CheckCircle, Database, Play, ShieldCheck, Workflow } from 'lucide-react';

const stats = [
  {
    name: 'Data Sources',
    value: '12',
    icon: Database,
    description: 'Active ingestion connections',
    trend: '+2 onboarding this week',
  },
  {
    name: 'Active Pipelines',
    value: '8',
    icon: Workflow,
    description: 'Running orchestration jobs',
    trend: '2 queued for deployment',
  },
  {
    name: 'Records Processed',
    value: '2.4M',
    icon: Activity,
    description: 'Processed over the last 24h',
    trend: '+12% compared to yesterday',
  },
  {
    name: 'Pipeline Success',
    value: '99.2%',
    icon: ShieldCheck,
    description: '7-day reliability baseline',
    trend: 'No critical incidents',
  },
];

const activityTimeline = [
  {
    title: 'Sales pipeline completed',
    details: '2 minutes ago • 15,234 records loaded',
    status: 'success',
  },
  {
    title: 'Customer sync validated',
    details: '15 minutes ago • 8,432 records updated',
    status: 'running',
  },
  {
    title: 'Inventory transform approved',
    details: '1 hour ago • 23,145 records processed',
    status: 'healthy',
  },
];

const quickActions = [
  'Run priority pipeline',
  'Register new data source',
  'Open AI pipeline workbench',
];

export default function DashboardPage() {
  return (
    <div className="space-y-5">
      <SectionHeader
        title="Operations Command Center"
        subtitle="Real-time execution visibility for ingestion, transformation, and pipeline health."
        metric="Live"
        actions={(
          <>
            <Button variant="outline" size="sm">
              View reports
            </Button>
            <Button size="sm">
              New pipeline
            </Button>
          </>
        )}
      />

      <div
        data-testid="ops-summary-bar"
        className="grid gap-3 rounded-xl border bg-surface-elevated/90 p-4 shadow-panel sm:grid-cols-2 xl:grid-cols-4"
      >
        {[
          { label: 'Cluster health', value: 'Healthy', status: 'healthy' },
          { label: 'Failed jobs', value: '1 incident', status: 'warning' },
          { label: 'SLA coverage', value: '99.9%', status: 'success' },
          { label: 'Avg. queue wait', value: '14s', status: 'running' },
        ].map((item) => (
          <div key={item.label} className="space-y-1 rounded-lg border bg-background/60 p-3">
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{item.label}</p>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{item.value}</p>
              <StatusBadge status={item.status} className="text-[10px] uppercase tracking-wide" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;

          return (
            <Card key={stat.name} className="border-border/80 bg-surface-elevated shadow-panel">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-2xl font-semibold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
                <p className="inline-flex items-center gap-1 text-xs text-status-success">
                  <ArrowUpRight className="h-3 w-3" />
                  {stat.trend}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        <Card className="border-border/80 bg-surface-elevated shadow-panel">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest orchestration and ingestion events</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activityTimeline.map((event) => (
              <div key={event.title} className="flex items-start gap-3 rounded-lg border bg-background/60 p-3">
                <div className="mt-0.5 rounded-full bg-status-success/15 p-1.5">
                  <CheckCircle className="h-4 w-4 text-status-success" />
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium">{event.title}</p>
                  <p className="text-xs text-muted-foreground">{event.details}</p>
                </div>
                <StatusBadge status={event.status} className="ml-auto text-[10px] uppercase tracking-wide" />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-surface-elevated shadow-panel">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>High-frequency operator shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {quickActions.map((label) => (
              <Button key={label} variant="outline" className="w-full justify-start gap-2 border-border/70">
                <Play className="h-3.5 w-3.5 text-primary" />
                {label}
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
